import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Lightning, Handshake, MapPin, CurrencyDollar, Calendar,
  CheckCircle, ArrowRight, ShareNetwork,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { withUtm } from "../utils/utm";

/**
 * Public, no-auth landing page for one sponsorship opportunity.
 * Nonprofits share this URL on Facebook, Rotary newsletters, LinkedIn, etc.
 * SMBs land here, see the ask, and sign up to express interest — every
 * external view is a free top-of-funnel lead.
 */
const SponsorPublic = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [opp, setOpp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/sponsorships/public/${id}`);
        setOpp(r.data);
      } catch (e) {
        setError(e.response?.status === 404 ? "This sponsorship is no longer available." : "Couldn't load this opportunity.");
      } finally { setLoading(false); }
    })();
  }, [id]);

  const handleInterest = () => {
    if (user) {
      // Send authenticated users straight to the main sponsorships page with this opp focused
      navigate(`/sponsorships?focus=${id}`);
    } else {
      // Drop unauth users into signup, then bounce back here so they land on the same opportunity
      localStorage.setItem("post_signup_redirect", `/sponsor/${id}`);
      navigate("/");
      setTimeout(() => {
        const evt = new CustomEvent("open-auth-modal");
        window.dispatchEvent(evt);
      }, 200);
    }
  };

  const copyShare = async () => {
    const url = withUtm(window.location.href, "share", "social", `sponsor_${id}`);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste it on Facebook, newsletters, anywhere");
    } catch {
      toast.error("Couldn't copy — long-press to copy manually");
    }
  };

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center text-zinc-500">Loading…</div>;
  if (error) return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center text-center px-6">
      <Handshake className="w-12 h-12 text-zinc-700 mb-3" />
      <p className="text-zinc-400 mb-4">{error}</p>
      <Link to="/sponsorships" className="text-emerald-400 underline">Browse other opportunities</Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-zinc-100" data-testid="sponsor-public-page">
      {/* Minimal header */}
      <header className="border-b border-zinc-900">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Lightning weight="fill" className="w-6 h-6 text-emerald-500" />
            <span className="font-bold tracking-tight">FlashGrants</span>
          </Link>
          <Button
            variant="outline"
            size="sm"
            onClick={copyShare}
            className="border-zinc-800 hover:border-emerald-500/40 hover:text-emerald-300 rounded-sm"
            data-testid="share-link-btn"
          >
            <ShareNetwork className="w-4 h-4 mr-1.5" /> Share
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-10">
        <div className="text-xs uppercase tracking-widest text-emerald-400 font-mono mb-3 flex items-center gap-2 flex-wrap">
          <span>Local sponsorship opportunity</span>
          {opp.category && opp.category !== "General" && (
            <span className="text-[10px] bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-sm px-2 py-0.5 font-mono">
              {opp.category}
            </span>
          )}
        </div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2" data-testid="opp-event-name">{opp.event_name}</h1>
        <p className="text-zinc-500 mb-6">{opp.org_name} · {opp.county} County, SC</p>

        <Card className="bg-zinc-950 border-zinc-800 mb-6">
          <CardContent className="py-6 space-y-5">
            <p className="text-zinc-200 leading-relaxed whitespace-pre-wrap">{opp.description}</p>

            <div className="grid sm:grid-cols-3 gap-3 pt-2 border-t border-zinc-900">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-0.5">Budget range</p>
                <p className="text-sm text-zinc-100 flex items-center gap-1"><CurrencyDollar weight="fill" className="w-4 h-4 text-emerald-400" />${opp.sponsorship_min.toLocaleString()}–${opp.sponsorship_max.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-0.5">Deadline</p>
                <p className="text-sm text-zinc-100 flex items-center gap-1"><Calendar className="w-4 h-4 text-zinc-400" />{opp.deadline}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-0.5">Location</p>
                <p className="text-sm text-zinc-100 flex items-center gap-1"><MapPin className="w-4 h-4 text-zinc-400" />{opp.county} County</p>
              </div>
            </div>

            {opp.benefits_offered?.length > 0 && (
              <div className="pt-2 border-t border-zinc-900">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-2">What sponsors receive</p>
                <ul className="space-y-1.5">
                  {opp.benefits_offered.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-zinc-200">
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {opp.industries_welcomed?.length > 0 && (
              <div className="pt-2 border-t border-zinc-900">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-2">Industries we'd love to partner with</p>
                <div className="flex flex-wrap gap-1.5">
                  {opp.industries_welcomed.map((i) => (
                    <span key={i} className="text-xs bg-zinc-900 border border-zinc-800 rounded-sm px-2 py-1 text-zinc-300">{i}</span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="space-y-3">
          <Button
            onClick={handleInterest}
            className="w-full sm:w-auto bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm px-6 py-5 text-base"
            data-testid="interested-cta"
          >
            <Handshake weight="fill" className="w-5 h-5 mr-2" />
            I'm interested in sponsoring
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-xs text-zinc-600">
            Free 30-second sign-up. {opp.org_name} gets your contact details by email — no middleman, no commission.
          </p>
        </div>

        {/* Soft footer pitch */}
        <div className="mt-12 pt-8 border-t border-zinc-900 text-sm text-zinc-500">
          <p className="mb-2">
            <strong className="text-zinc-300">FlashGrants</strong> is South Carolina's marketplace where nonprofits and local businesses find each other.
          </p>
          <p>
            <Link to="/sponsorships" className="text-emerald-400 hover:text-emerald-300 underline">Browse other opportunities →</Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default SponsorPublic;
